import AddRounded from '@mui/icons-material/AddRounded';
import ContentCopyRounded from '@mui/icons-material/ContentCopyRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useState, type ReactNode } from 'react';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { VariantPicker, type Variant } from '../../components/pickers';
import { useNotify } from '../../lib/notify';
import { useLocations } from '../inventory/model';
import {
  useFoodpandaSettings,
  useFoodpandaStatus,
  useOutletActions,
  useSimulateOrder,
  type FoodpandaStatus,
  type UpdateFoodpanda,
} from './api';

type Environment = NonNullable<UpdateFoodpanda['environment']>;

const ENVIRONMENTS: { value: Environment; label: string; hint: string }[] = [
  {
    value: 'SIMULATOR',
    label: 'Simulator',
    hint: 'Try it with test orders; nothing reaches Foodpanda',
  },
  { value: 'STAGING', label: 'Foodpanda test (staging)', hint: 'Foodpanda’s test system' },
  { value: 'PRODUCTION', label: 'Live', hint: 'Real orders from your customers' },
];

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
        <Typography variant="h6">{title}</Typography>
        {hint && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {hint}
          </Typography>
        )}
        {children}
      </CardContent>
    </Card>
  );
}

/** Set-up (foodpanda.manage): the shop switch, how orders are handled, the API, outlets. */
export function SetupTab({ status }: { status: FoodpandaStatus }) {
  const notify = useNotify();
  const save = useFoodpandaSettings();
  const conn = status.connection;
  const [form, setForm] = useState({
    environment: conn?.environment ?? 'SIMULATOR',
    baseUrl: conn?.baseUrl ?? '',
    username: conn?.username ?? '',
    password: '',
    chainCode: conn?.chainCode ?? '',
    autoAccept: conn?.autoAccept ?? false,
    defaultPrepMinutes: String(conn?.defaultPrepMinutes ?? 20),
    invoiceOnPickup: conn?.invoiceOnPickup ?? true,
    autoAvailability: conn?.autoAvailability ?? true,
  });
  const [rotate, setRotate] = useState(false);
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [k]: v }));
  const prep = Number(form.defaultPrepMinutes);
  const prepOk = Number.isInteger(prep) && prep >= 1 && prep <= 240;

  const submit = (extra: UpdateFoodpanda = {}) =>
    save
      .mutateAsync({
        environment: form.environment,
        baseUrl: form.baseUrl.trim() || null,
        username: form.username.trim() || null,
        ...(form.password ? { password: form.password } : {}),
        chainCode: form.chainCode.trim() || null,
        autoAccept: form.autoAccept,
        defaultPrepMinutes: prep,
        invoiceOnPickup: form.invoiceOnPickup,
        autoAvailability: form.autoAvailability,
        ...extra,
      })
      .then(() => {
        set('password', '');
        notify.success(conn ? 'Foodpanda settings saved' : 'Foodpanda connected');
      }, notify.error);

  return (
    <Stack spacing={2.5}>
      <Section title="Foodpanda in this shop">
        <FormControlLabel
          control={
            <Switch
              checked={status.plugin.shopEnabled}
              disabled={save.isPending}
              onChange={(e) =>
                save
                  .mutateAsync({ shopEnabled: e.target.checked })
                  .then(
                    (s) => notify.success(s.plugin.shopEnabled ? 'Foodpanda on' : 'Foodpanda off'),
                    notify.error,
                  )
              }
            />
          }
          label={
            status.plugin.shopEnabled
              ? 'On: orders from Foodpanda are taken'
              : 'Off: Foodpanda orders are refused'
          }
        />
      </Section>

      <Section
        title="Orders"
        hint="Orders become sales orders to the “Foodpanda” customer; accepting reserves the stock."
      >
        <Stack spacing={2} sx={{ maxWidth: 520 }}>
          <FormControlLabel
            control={
              <Switch
                checked={form.autoAccept}
                onChange={(e) => set('autoAccept', e.target.checked)}
              />
            }
            label="Accept new orders automatically"
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.invoiceOnPickup}
                onChange={(e) => set('invoiceOnPickup', e.target.checked)}
              />
            }
            label="Deliver and invoice when the order is picked up"
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.autoAvailability}
                onChange={(e) => set('autoAvailability', e.target.checked)}
              />
            }
            label="Take items off Foodpanda when they run out of stock"
          />
          <TextField
            label="Usual preparation time"
            value={form.defaultPrepMinutes}
            onChange={(e) => set('defaultPrepMinutes', e.target.value.replace(/\D/g, ''))}
            error={!prepOk}
            helperText={prepOk ? 'Offered when accepting an order' : 'From 1 to 240 minutes'}
            slotProps={{
              input: { endAdornment: <InputAdornment position="end">min</InputAdornment> },
              htmlInput: { inputMode: 'numeric' },
            }}
            sx={{ maxWidth: 220 }}
          />
          {conn && (
            <Typography variant="body2" color="text.secondary">
              Invoiced to customer: <b>{conn.contact.name}</b>
            </Typography>
          )}
        </Stack>
      </Section>

      <Section
        title="Connection"
        hint="Foodpanda gives these details once your shop is approved for the POS integration."
      >
        <Stack spacing={2} sx={{ maxWidth: 520 }}>
          <TextField
            select
            label="Mode"
            value={form.environment}
            onChange={(e) => set('environment', e.target.value as Environment)}
            helperText={ENVIRONMENTS.find((x) => x.value === form.environment)?.hint}
          >
            {ENVIRONMENTS.map((x) => (
              <MenuItem key={x.value} value={x.value}>
                {x.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="API address"
            placeholder="https://…"
            value={form.baseUrl}
            onChange={(e) => set('baseUrl', e.target.value)}
          />
          <TextField
            label="API user name"
            value={form.username}
            onChange={(e) => set('username', e.target.value)}
            autoComplete="off"
          />
          <TextField
            label="API password"
            type="password"
            value={form.password}
            onChange={(e) => set('password', e.target.value)}
            autoComplete="new-password"
            helperText={conn?.hasPassword ? 'Saved. Type a new one only to change it.' : undefined}
          />
          <TextField
            label="Chain code (optional)"
            value={form.chainCode}
            onChange={(e) => set('chainCode', e.target.value)}
          />
          <Box>
            <Button
              variant="contained"
              disabled={save.isPending || !prepOk}
              onClick={() => void submit()}
            >
              {conn ? 'Save' : 'Connect Foodpanda'}
            </Button>
          </Box>
        </Stack>
      </Section>

      {conn?.webhook && (
        <Section
          title="Details to give Foodpanda"
          hint="Foodpanda sends your orders to this address, signing in with this user name and password."
        >
          <Stack spacing={1.5} sx={{ maxWidth: 640 }}>
            <CopyField label="Plugin address" value={conn.webhook.baseUrl} />
            <CopyField label="User name" value={conn.webhook.username} />
            <CopyField label="Password" value={conn.webhook.password} secret />
            <Box>
              <Button color="warning" onClick={() => setRotate(true)}>
                New password
              </Button>
            </Box>
          </Stack>
        </Section>
      )}
      {rotate && (
        <ConfirmDialog
          open
          title="Make a new password?"
          body="Foodpanda can’t send orders until you give them the new password."
          confirmLabel="Make new password"
          onClose={() => setRotate(false)}
          onConfirm={() => submit({ rotateWebhookSecret: true })}
        />
      )}

      {conn && <Outlets status={status} />}
    </Stack>
  );
}

function CopyField({ label, value, secret }: { label: string; value: string; secret?: boolean }) {
  const notify = useNotify();
  const [shown, setShown] = useState(!secret);
  return (
    <TextField
      label={label}
      value={shown ? value : '•'.repeat(16)}
      size="small"
      slotProps={{
        input: {
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              {secret && (
                <Button size="small" onClick={() => setShown((s) => !s)}>
                  {shown ? 'Hide' : 'Show'}
                </Button>
              )}
              <IconButton
                aria-label={`Copy ${label.toLowerCase()}`}
                onClick={() =>
                  void navigator.clipboard
                    .writeText(value)
                    .then(() => notify.success(`${label} copied`), notify.error)
                }
              >
                <ContentCopyRounded fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        },
      }}
    />
  );
}

function Outlets({ status }: { status: FoodpandaStatus }) {
  const notify = useNotify();
  const locations = useLocations();
  const { create, update } = useOutletActions();
  const [adding, setAdding] = useState(false);
  const [locationId, setLocationId] = useState('');
  const [vendorCode, setVendorCode] = useState('');
  const linked = new Set(status.outlets.map((o) => o.location.id));
  const free = (locations.data ?? []).filter(
    (l) => l.isActive && l.isSellable && !linked.has(l.id),
  );
  return (
    <Section
      title="Outlets"
      hint="Each Foodpanda outlet is one of your locations: its orders take stock from there. Give Foodpanda the vendor id."
    >
      <Stack spacing={1.5}>
        {status.outlets.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No outlet yet.
          </Typography>
        )}
        {status.outlets.map((o) => (
          <Stack
            key={o.id}
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            sx={{ alignItems: { sm: 'center' }, py: 1, borderBottom: 1, borderColor: 'divider' }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 600 }}>{o.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                Location {o.location.name} · vendor id <b>{o.remoteId}</b>
                {o.vendorCode ? ` · Foodpanda code ${o.vendorCode}` : ''}
              </Typography>
            </Box>
            <Chip
              size="small"
              label={o.isActive ? 'Taking orders' : 'Paused'}
              color={o.isActive ? 'success' : 'default'}
              variant="outlined"
            />
            <Button
              size="small"
              disabled={update.isPending}
              onClick={() =>
                update
                  .mutateAsync({ id: o.id, version: o.version, isActive: !o.isActive })
                  .then(
                    () => notify.success(o.isActive ? 'Outlet paused' : 'Outlet active'),
                    notify.error,
                  )
              }
            >
              {o.isActive ? 'Pause' : 'Activate'}
            </Button>
          </Stack>
        ))}
        <Box>
          <Button
            startIcon={<AddRounded />}
            onClick={() => setAdding(true)}
            disabled={!free.length}
          >
            Link a location
          </Button>
        </Box>
      </Stack>
      {adding && (
        <Dialog open onClose={() => setAdding(false)} fullWidth maxWidth="xs">
          <DialogTitle>Link a location to Foodpanda</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                select
                label="Location"
                value={locationId}
                onChange={(e) => setLocationId(e.target.value)}
              >
                {free.map((l) => (
                  <MenuItem key={l.id} value={l.id}>
                    {l.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Foodpanda vendor code (optional)"
                value={vendorCode}
                onChange={(e) => setVendorCode(e.target.value)}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAdding(false)}>Cancel</Button>
            <Button
              variant="contained"
              disabled={!locationId || create.isPending}
              onClick={() =>
                create
                  .mutateAsync({ locationId, vendorCode: vendorCode.trim() || null })
                  .then(() => {
                    notify.success('Outlet linked');
                    setAdding(false);
                    setLocationId('');
                    setVendorCode('');
                  }, notify.error)
              }
            >
              Link
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Section>
  );
}

interface SimLine {
  variant: Variant | null;
  qty: string;
}

/** Simulator: a made-up order travels the same path as a real Foodpanda order. */
export function SimulatorDialog({ onClose }: { onClose: () => void }) {
  const notify = useNotify();
  const simulate = useSimulateOrder();
  const outlets = (useFoodpandaStatus().data?.outlets ?? []).filter((o) => o.isActive);
  const [outletId, setOutletId] = useState(outlets[0]?.id ?? '');
  const [lines, setLines] = useState<SimLine[]>([{ variant: null, qty: '1' }]);
  const [customerName, setCustomerName] = useState('');
  const [shopDiscount, setShopDiscount] = useState('');
  const [platformDiscount, setPlatformDiscount] = useState('');
  const ready = outletId && lines.every((l) => l.variant && Number(l.qty) >= 1);
  const patch = (i: number, p: Partial<SimLine>) =>
    setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...p } : l)));
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Send a test order</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="info">
            Pick products as a Foodpanda customer would. Their SKU is the Foodpanda item code.
          </Alert>
          {outlets.length > 1 && (
            <TextField
              select
              label="Outlet"
              value={outletId}
              onChange={(e) => setOutletId(e.target.value)}
            >
              {outlets.map((o) => (
                <MenuItem key={o.id} value={o.id}>
                  {o.name}
                </MenuItem>
              ))}
            </TextField>
          )}
          {lines.map((l, i) => (
            <Stack key={i} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Box sx={{ flex: 1 }}>
                <VariantPicker
                  value={l.variant}
                  onChange={(variant) => patch(i, { variant })}
                  label={`Item ${String(i + 1)}`}
                />
              </Box>
              <TextField
                label="Qty"
                value={l.qty}
                onChange={(e) => patch(i, { qty: e.target.value.replace(/\D/g, '') })}
                sx={{ width: 80 }}
                slotProps={{ htmlInput: { inputMode: 'numeric' } }}
              />
              <IconButton
                aria-label={`Remove item ${String(i + 1)}`}
                disabled={lines.length === 1}
                onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}
              >
                <DeleteOutlineRounded />
              </IconButton>
            </Stack>
          ))}
          <Box>
            <Button
              startIcon={<AddRounded />}
              onClick={() => setLines((ls) => [...ls, { variant: null, qty: '1' }])}
              disabled={lines.length >= 20}
            >
              Add item
            </Button>
          </Box>
          <TextField
            label="Customer name (optional)"
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              label="Your voucher"
              value={shopDiscount}
              onChange={(e) => setShopDiscount(e.target.value.replace(/[^\d.]/g, ''))}
              helperText="Paid by the shop"
              fullWidth
            />
            <TextField
              label="Foodpanda voucher"
              value={platformDiscount}
              onChange={(e) => setPlatformDiscount(e.target.value.replace(/[^\d.]/g, ''))}
              helperText="Paid by Foodpanda"
              fullWidth
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!ready || simulate.isPending}
          onClick={() =>
            simulate
              .mutateAsync({
                outletId,
                lines: lines.map((l) => ({ sku: l.variant?.sku ?? '', qty: Number(l.qty) })),
                ...(customerName.trim() ? { customerName: customerName.trim() } : {}),
                ...(Number(shopDiscount) > 0 ? { shopDiscount: Number(shopDiscount) } : {}),
                ...(Number(platformDiscount) > 0
                  ? { platformDiscount: Number(platformDiscount) }
                  : {}),
              })
              .then((o) => {
                if (o.status === 'FAILED') notify.info(o.error ?? 'The order needs attention');
                else notify.success(`Test order #${o.code} received`);
                onClose();
              }, notify.error)
          }
        >
          Send order
        </Button>
      </DialogActions>
    </Dialog>
  );
}
